import { useEffect, useMemo, useState } from 'react';
import { Upload, Link2, ShieldCheck, ShieldAlert, ShieldX, HelpCircle, Copy, Check } from 'lucide-react';
import { Alert, Badge, Button, Card, Input, Loader, Select, Switch } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import DataTable from '@/components/DataTable';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * Prescription Verification (`/app-prescription-check`) — upload a prescription image or
 * point at a URL, run it through one of the company's own AI Model Provider connections,
 * and see the structured result: clinic, GSTIN, address and the medicines prescribed.
 *
 * Verifying is gated on `tools:run` (it spends the company's provider credits); the
 * history table below is a plain `tools:view` read over app_prescription_checks.
 *
 * The history deliberately shows only the verdict-level columns. The patient-identifying
 * extraction is stored per-request and only when "Store full extraction" was on, so most
 * rows genuinely have nothing more to show — see docs/FEATURES.md on the PII stance.
 */
const PAGE_SIZE = 20;

const VERDICTS = {
  valid: { label: 'Valid prescription', variant: 'success', Icon: ShieldCheck },
  likely_valid: { label: 'Likely valid', variant: 'warning', Icon: ShieldAlert },
  not_a_prescription: { label: 'Not a prescription', variant: 'error', Icon: ShieldX },
  unreadable: { label: 'Unreadable', variant: 'secondary', Icon: HelpCircle },
};

/** The machine-readable warning codes, said in words a human can act on. */
const WARNING_TEXT = {
  gstin_absent: 'No GSTIN printed (normal for a prescription)',
  gstin_invalid_checksum: 'A GSTIN was read but failed its checksum — discarded',
  no_registration_number: 'No medical registration number',
  no_signature_or_stamp: 'No signature or stamp',
  no_issue_date: 'No issue date',
  handwritten_low_legibility: 'Handwritten — read every line against the original',
  poor_image_quality: 'Poor image quality',
  low_confidence_medication: 'At least one medicine was read with low confidence',
  illegible_medication_name: 'At least one medicine name was not legible',
};

function VerdictBadge({ verdict, size = 'sm' }) {
  const v = VERDICTS[verdict] || { label: verdict || '—', variant: 'secondary', Icon: HelpCircle };
  return <Badge variant={v.variant} size={size}>{v.label}</Badge>;
}

const pct = (n) => (typeof n === 'number' ? `${Math.round(n * 100)}%` : '—');

/** "1-0-1"-style summary of the parsed dosing slots. */
function doseSummary(m) {
  const f = m.frequency_normalized;
  if (!f) return m.frequency || '—';
  const parts = [f.morning, f.afternoon, f.evening, f.night].map((x) => (x == null ? 0 : x));
  return parts.join('-');
}

const FOOD_TEXT = {
  before_food: 'before food', after_food: 'after food',
  with_food: 'with food', empty_stomach: 'empty stomach',
};

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-secondary-500">{label}</div>
      <div className={`text-sm ${mono ? 'font-mono' : ''}`}>{value || <span className="text-secondary-400">—</span>}</div>
    </div>
  );
}

function MedicationsTable({ medications }) {
  if (!medications.length) {
    return (
      <div>
        <h3 className="font-semibold mb-2">Medicines prescribed (0)</h3>
        <p className="text-sm text-secondary-500">No medicines were read from this document.</p>
      </div>
    );
  }
  return (
    <div>
      <h3 className="font-semibold mb-2">Medicines prescribed ({medications.length})</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-secondary-500">
            <tr className="border-b border-[var(--app-border)]">
              <th className="text-left py-1.5 pr-3">#</th>
              <th className="text-left py-1.5 pr-3">Medicine</th>
              <th className="text-left py-1.5 pr-3">Strength</th>
              <th className="text-left py-1.5 pr-3">Dose (M-A-E-N)</th>
              <th className="text-left py-1.5 pr-3">Timing</th>
              <th className="text-left py-1.5 pr-3">Duration</th>
              <th className="text-left py-1.5 pr-3">Qty</th>
              <th className="text-left py-1.5">Read</th>
            </tr>
          </thead>
          <tbody>
            {medications.map((m) => (
              <tr key={m.line_no} className="border-b border-[var(--app-border)]/50 align-top">
                <td className="py-1.5 pr-3 text-secondary-500">{m.line_no}</td>
                <td className="py-1.5 pr-3">
                  <div className="font-medium">{m.name || <span className="text-red-500">not legible</span>}</div>
                  {/* The verbatim line, so a reviewer can judge the reading itself. */}
                  <div className="text-xs text-secondary-500 font-mono">{m.raw}</div>
                </td>
                <td className="py-1.5 pr-3">{m.strength || '—'}</td>
                <td className="py-1.5 pr-3 font-mono">{doseSummary(m)}</td>
                <td className="py-1.5 pr-3">{FOOD_TEXT[m.food_relation] || '—'}</td>
                <td className="py-1.5 pr-3">{m.duration || '—'}</td>
                <td className="py-1.5 pr-3">{m.quantity || '—'}</td>
                <td className="py-1.5">
                  <Badge variant={(m.confidence ?? 0) >= 0.75 ? 'success' : 'warning'} size="sm">{pct(m.confidence)}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ResultPanel({ result }) {
  const { clinic = {}, prescriber = {}, patient = {}, medications = [], pharmacy } = result;
  const address = clinic.address || {};
  return (
    <Card className="p-3 space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <VerdictBadge verdict={result.verdict} size="md" />
        <span className="text-sm text-secondary-500">confidence {pct(result.confidence)}</span>
        {result.document_type && result.document_type !== 'prescription' && (
          <Badge variant="secondary" size="sm">detected: {result.document_type.replace(/_/g, ' ')}</Badge>
        )}
        {result.cached && <Badge variant="secondary" size="sm">cached result — not re-charged</Badge>}
        <span className="ml-auto text-xs text-secondary-500 font-mono">
          {result.meta?.provider}/{result.meta?.model} · {result.meta?.duration_ms}ms
        </span>
      </div>

      {result.needs_human_review && (
        <Alert variant="warning" title="Check this against the original document">
          {result.disclaimer}
        </Alert>
      )}

      {!!(result.reasons || []).length && (
        <ul className="text-sm text-secondary-600 list-disc pl-5 space-y-0.5">
          {result.reasons.map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      )}

      {/* Clinic / GSTIN / Address — the four fields this screen exists to surface. */}
      <div>
        <h3 className="font-semibold mb-2">Clinic / Hospital</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Name" value={clinic.name} />
          <Field label="Name (other script)" value={clinic.name_alt} />
          <Field
            label="GSTIN"
            mono
            value={clinic.gstin || pharmacy?.gstin || (
              <span className="text-secondary-400" title="GSTIN is a tax identifier printed on pharmacy bills, not on prescriptions">not printed</span>
            )}
          />
          <Field label="Phone" value={clinic.phone} />
          <div className="sm:col-span-2 lg:col-span-3">
            <Field label="Address" value={address.full || [address.line1, address.city, address.state, address.pincode].filter(Boolean).join(', ')} />
          </div>
          <Field label="Timings" value={clinic.timings} />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Prescriber" value={prescriber.name} />
        <Field label="Qualifications" value={(prescriber.qualifications || []).join(', ')} />
        <Field label="Reg. number" value={prescriber.registration_number} mono />
        <Field label="Signed / stamped" value={prescriber.signature_present ? 'Yes' : 'No'} />
        <Field label="Patient" value={patient.name} />
        <Field label="Age / sex" value={[patient.age, patient.sex].filter(Boolean).join(' / ')} />
        <Field label="Issued" value={result.issued_at} />
        <Field label="Follow-up" value={result.follow_up_date} />
      </div>

      <MedicationsTable medications={medications} />

      {!!(result.diagnosis || []).length && <Field label="Diagnosis / complaints" value={result.diagnosis.join('; ')} />}
      {!!(result.advice || []).length && <Field label="Advice" value={result.advice.join('; ')} />}

      {!!(result.warnings || []).length && (
        <div>
          <h3 className="font-semibold mb-2">Warnings</h3>
          <ul className="text-sm space-y-1">
            {result.warnings.map((w) => (
              <li key={w} className="text-secondary-600">• {WARNING_TEXT[w] || w}</li>
            ))}
          </ul>
        </div>
      )}

      {result.raw_text && (
        <details className="text-sm">
          <summary className="cursor-pointer text-secondary-600">Full transcription</summary>
          <pre className="mt-2 whitespace-pre-wrap bg-[var(--app-surface)] rounded p-3 text-xs">{result.raw_text}</pre>
        </details>
      )}
      <details className="text-sm">
        <summary className="cursor-pointer text-secondary-600">Raw JSON (what the API returns)</summary>
        <pre className="mt-2 bg-[var(--app-surface)] rounded p-3 text-xs overflow-x-auto">{JSON.stringify(result, null, 2)}</pre>
      </details>
    </Card>
  );
}

/** Single-quote for a POSIX shell, so a connection name with spaces or an apostrophe
 *  produces a command that can actually be pasted. */
function shellQuote(value) {
  return `'${String(value).replace(/'/g, "'\\''")}'`;
}

/**
 * The public-API equivalent of the test that was just run, built from what was actually
 * submitted. The screen doubles as the integration guide: try it here, then copy the
 * exact call for your own systems. The token is a placeholder — a real tnt_ token is
 * minted on the API Tokens screen with the `tools:run` scope.
 */
function buildCurl({ mode, connectionName, model, storeExtraction, imageUrl, fileName }) {
  const origin = typeof window === 'undefined' ? 'https://YOUR_HOST' : window.location.origin;
  const auth = '  -H "Authorization: Bearer tnt_YOUR_TOKEN"';
  if (mode === 'upload') {
    const lines = [
      `curl -X POST ${origin}/api/v1/prescriptions/verify/upload`,
      auth,
      `  -F file=@${fileName || 'prescription.jpg'}`,
    ];
    if (connectionName) lines.push(`  -F connection_name=${shellQuote(connectionName)}`);
    if (model) lines.push(`  -F model=${shellQuote(model)}`);
    if (storeExtraction) lines.push('  -F store_extraction=true');
    return lines.join(' \\\n');
  }
  const body = { image_url: imageUrl || 'https://example.com/prescription.jpg' };
  if (connectionName) body.connection_name = connectionName;
  if (model) body.model = model;
  if (storeExtraction) body.store_extraction = true;
  return [
    `curl -X POST ${origin}/api/v1/prescriptions/verify`,
    auth,
    '  -H "Content-Type: application/json"',
    `  -d ${shellQuote(JSON.stringify(body))}`,
  ].join(' \\\n');
}

function CurlSnippet({ command }) {
  const [copied, setCopied] = useState(false);
  // Local state rather than the toast provider: this is a two-second affordance next to
  // the button, and it must not depend on a toast being mounted.
  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-semibold">Call this from your own system</h3>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => navigator.clipboard?.writeText(command).then(() => setCopied(true)).catch(() => {})}
        >
          {copied ? <><Check size={14} className="mr-1 inline" /> Copied</> : <><Copy size={14} className="mr-1 inline" /> Copy</>}
        </Button>
      </div>
      <p className="text-xs text-secondary-500 mb-2">
        The same check over the public API. Mint a token with the <code>tools:run</code> scope on the
        API Tokens screen and replace <code>tnt_YOUR_TOKEN</code>.
      </p>
      <pre className="bg-[var(--app-surface)] rounded p-3 text-xs overflow-x-auto">{command}</pre>
    </div>
  );
}

/** What is about to be tested (or what produced the result on screen). */
function ImagePreview({ src, label = 'Selected image' }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [src]);
  if (!src || broken) return null;
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-secondary-500 mb-1">{label}</div>
      <img
        src={src}
        alt={label}
        onError={() => setBroken(true)}
        className="max-h-40 rounded border border-[var(--app-border)]"
      />
    </div>
  );
}

function VerifyForm({ connections, onResult }) {
  const { fetchApi } = useAppApi();
  const [mode, setMode] = useState('upload');
  const [file, setFile] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [connectionName, setConnectionName] = useState('');
  const [storeExtraction, setStoreExtraction] = useState(false);
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [filePreview, setFilePreview] = useState('');

  // Revoke on change/unmount — without this every re-pick leaks a blob URL for the
  // lifetime of the document.
  useEffect(() => {
    if (!file) { setFilePreview(''); return undefined; }
    const url = URL.createObjectURL(file);
    setFilePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function onDrop(e) {
    e.preventDefault();
    setDragActive(false);
    // Only a UX guard: the server sniffs magic bytes and rejects anything that is not
    // really an image (services/prescriptions/imageInput.js).
    const dropped = [...(e.dataTransfer?.files || [])].find((f) => f.type.startsWith('image/'));
    if (dropped) setFile(dropped);
  }

  // Preselect when there is only one connection — that is also the only case the server
  // resolves without a name, so the form and the API agree on what "unambiguous" means.
  useEffect(() => {
    if (connections.length === 1) setConnectionName(connections[0].name);
  }, [connections]);

  const options = connections.map((c) => ({ label: `${c.name} (${c.provider})`, value: c.name }));

  async function submit() {
    setErr('');
    setBusy(true);
    try {
      let payload;
      if (mode === 'upload') {
        if (!file) throw new Error('Choose an image first.');
        payload = new FormData();
        payload.append('file', file);
        if (connectionName) payload.append('connection_name', connectionName);
        payload.append('store_extraction', String(storeExtraction));
        payload.append('force', String(force));
      } else {
        if (!imageUrl.trim()) throw new Error('Paste an image URL first.');
        payload = {
          image_url: imageUrl.trim(),
          connection_name: connectionName || undefined,
          store_extraction: storeExtraction,
          force,
        };
      }
      const res = await fetchApi('prescriptionVerify', payload);
      onResult(res, {
        mode, connectionName, storeExtraction, imageUrl: imageUrl.trim(),
        preview: mode === 'upload' ? filePreview : imageUrl.trim(),
      });
    } catch (e) {
      // The server's {error, code} body is the useful message; the axios text is not.
      setErr(e?.response?.data?.error || e.message || 'Verification failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Card className="p-3 space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Test this service</h2>
        <p className="text-sm text-secondary-500">
          Upload a prescription image or paste a URL, pick a connection, and run a live check.
          This is the same code path the public API uses.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <Button size="sm" variant={mode === 'upload' ? 'primary' : 'secondary'} onClick={() => setMode('upload')}>
          <Upload size={14} className="mr-1 inline" /> Upload image
        </Button>
        <Button size="sm" variant={mode === 'url' ? 'primary' : 'secondary'} onClick={() => setMode('url')}>
          <Link2 size={14} className="mr-1 inline" /> From URL
        </Button>
      </div>

      {mode === 'upload' ? (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
          className={`rounded border-2 border-dashed p-4 transition-colors ${dragActive ? 'border-primary-500 bg-primary-500/5' : 'border-[var(--app-border)]'}`}
        >
          <label className="block text-sm mb-1">Prescription image</label>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm file:mr-3 file:rounded file:border-0 file:bg-[var(--app-border)]/40 file:px-3 file:py-1.5"
          />
          <p className="text-xs text-secondary-500 mt-1">
            Drag an image here, or choose one. JPEG, PNG, WebP or GIF — the format is detected from
            the file contents, not its name.
          </p>
          {filePreview && <div className="mt-3"><ImagePreview src={filePreview} /></div>}
        </div>
      ) : (
        <div className="space-y-3">
          <Input
            label="Image URL"
            placeholder="https://example.com/prescription.jpg"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
          />
          {imageUrl.trim() && <ImagePreview src={imageUrl.trim()} />}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <Select
          label="AI Model Provider connection"
          options={options}
          value={connectionName}
          onChange={setConnectionName}
          placeholder={connections.length ? 'Choose a connection' : 'No AI Model connections'}
          searchable
        />
        <div className="flex flex-col justify-end gap-3 pb-1">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Switch checked={storeExtraction} onChange={setStoreExtraction} />
            <span>Store full extraction <span className="text-secondary-500">(patient data)</span></span>
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Switch checked={force} onChange={setForce} />
            <span>Re-run even if this image was checked before</span>
          </label>
        </div>
      </div>

      <p className="text-xs text-secondary-500">
        The call is billed to the selected connection&apos;s own API key. Identical images are answered
        from cache for 24 hours unless you re-run. Storing the full extraction persists patient
        names, diagnoses and medicines — leave it off unless you need the audit copy.
      </p>

      {err && <Alert variant="error">{err}</Alert>}

      <div className="text-right">
        <Button onClick={submit} disabled={busy || !connections.length}>
          {busy ? 'Verifying…' : 'Verify prescription'}
        </Button>
      </div>
      </Card>

      {/* Always on screen, not gated behind a run: the point of the snippet is to be
          copyable at any time, and it tracks the live form state so it always matches
          what the button above would do. */}
      <Card className="p-3">
        <CurlSnippet
          command={buildCurl({
            mode,
            connectionName,
            storeExtraction,
            imageUrl: imageUrl.trim(),
            fileName: file?.name,
          })}
        />
      </Card>
    </>
  );
}

export default function PrescriptionsPage() {
  const { fetchApi } = useAppApi();
  const [connections, setConnections] = useState([]);
  const [connErr, setConnErr] = useState('');
  const [result, setResult] = useState(null);
  const [submitted, setSubmitted] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    fetchApi('prescriptionConnections')
      .then((res) => setConnections(res.connections || []))
      // A 403 here just means this user may view history but not run verifications.
      .catch((e) => setConnErr(e?.response?.data?.error || 'Could not load connections'));
  }, []);

  useEffect(() => {
    setLoading(true);
    setErr('');
    Promise.all([
      fetchApi('crud', {
        model: 'AppPrescriptionCheck', action: 'list',
        orderBy: { column: 'id', direction: 'desc' },
        limit: PAGE_SIZE, offset: page * PAGE_SIZE,
      }),
      fetchApi('crud', { model: 'AppPrescriptionCheck', action: 'count' }),
    ])
      .then(([list, count]) => {
        setRows((Array.isArray(list) ? list : list?.data) || []);
        setTotal(Number(count?.count ?? count?.total ?? count) || 0);
      })
      .catch((e) => setErr(e?.response?.data?.error || 'Could not load history'))
      .finally(() => setLoading(false));
  }, [page, reloadKey]);

  const columns = useMemo(() => [
    { key: 'verdict', label: 'Verdict', render: (r) => <VerdictBadge verdict={r.verdict} /> },
    { key: 'clinic_name', label: 'Clinic / Hospital', render: (r) => r.clinic_name || <span className="text-secondary-400">—</span> },
    { key: 'gstin', label: 'GSTIN', render: (r) => <span className="font-mono text-xs">{r.gstin || '—'}</span> },
    { key: 'medication_count', label: 'Medicines', render: (r) => r.medication_count ?? '—' },
    {
      key: 'model',
      label: 'Provider / model',
      render: (r) => (
        <div className="whitespace-nowrap">
          <div className="font-mono text-xs">{r.model || '—'}</div>
          <div className="text-xs text-secondary-500">{r.connection_name || r.provider}</div>
        </div>
      ),
    },
    { key: 'source', label: 'Source', render: (r) => <Badge variant="secondary" size="sm">{r.source}</Badge> },
    {
      key: 'status',
      label: 'Status',
      render: (r) => (r.status === 'succeeded'
        ? <span className="text-xs text-secondary-500">{r.duration_ms}ms</span>
        : <Badge variant={r.status === 'failed' ? 'error' : 'secondary'} size="sm">{r.status}</Badge>),
    },
    {
      key: 'created_at',
      label: 'When',
      render: (r) => <span className="whitespace-nowrap text-xs text-secondary-500">{r.created_at ? new Date(r.created_at).toLocaleString() : ''}</span>,
    },
  ], []);

  return (
    <PageShell className="space-y-3">
      <PageHeader
        title="Prescription Verification"
        subtitle={(
          <>
            Check whether a document is a genuine doctor&apos;s prescription and pull out the clinic,
            GSTIN, address and medicines prescribed. The same check is available to your own systems
            over the public API — mint a token with the <code>tools:run</code> scope on the API Tokens
            screen.
          </>
        )}
      />

      <Can module="tools" action="run" fallback={<Alert variant="info">You can view past checks, but running a new verification needs the <code>tools.run.org</code> permission.</Alert>}>
        {connErr && <Alert variant="warning" className="mb-3">{connErr}</Alert>}
        {!connErr && !connections.length && (
          <Alert variant="warning" className="mb-3">
            No active <strong>AI Model Provider</strong> connection for this company. Add one on the
            Connections screen first — verification runs on your own provider key.
          </Alert>
        )}
        <VerifyForm
          connections={connections}
          onResult={(r, ctx) => { setResult(r); setSubmitted(ctx); setReloadKey((k) => k + 1); }}
        />
      </Can>

      {result && (
        <>
          {submitted?.preview && (
            <Card className="p-3">
              <ImagePreview src={submitted.preview} label="Document tested" />
            </Card>
          )}
          <ResultPanel result={result} />
        </>
      )}

      <div>
        <h2 className="font-semibold mb-2">History</h2>
        {loading && !rows.length ? <Loader /> : (
          <DataTable
            data={rows}
            columns={columns}
            loading={loading}
            error={err}
            emptyText="No prescriptions checked yet."
            serverSide
            totalRecords={total}
            pageSize={PAGE_SIZE}
            pageNumber={page + 1}
            onPageChange={(p) => setPage(Math.max(0, p - 1))}
          />
        )}
      </div>
    </PageShell>
  );
}
